import React, { useState, useEffect, useCallback } from 'react';
import { X, Download, Smartphone, Share } from 'lucide-react';
import { safeLocalStorage } from '../utils/safeUtils';
import { IS_NATIVE } from '../utils/http';

/**
 * Composant de prompt d'installation PWA
 * Affiche une bannière pour encourager l'installation de l'app
 * Note: Ce composant n'est pas actif sur les apps natives
 */
export default function PWAInstallPrompt() {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(() => {
    // Vérifier si déjà installé au montage
    if (IS_NATIVE) return true; // Sur native, considérer comme déjà installé
    try {
      return window.matchMedia('(display-mode: standalone)').matches;
    } catch (e) {
      return false;
    }
  });

  useEffect(() => {
    // Ne pas afficher sur les apps natives
    if (IS_NATIVE || isInstalled) return;

    // Vérifier si iOS
    const isIOSDevice = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(isIOSDevice);

    // Vérifier si déjà refusé récemment
    const dismissedAt = safeLocalStorage.getItem('pwa-prompt-dismissed');
    if (dismissedAt) {
      const daysSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismissed < 7) return; // Ne pas réafficher pendant 7 jours
    }

    // Écouter l'événement beforeinstallprompt (Chrome, Edge, etc.)
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Afficher après un délai
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Pour iOS, afficher le prompt après un délai
    if (isIOSDevice && !navigator.standalone) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 5000);
    }

    // Écouter si l'app est installée
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setShowPrompt(false);
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [isInstalled]);

  const handleInstall = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === 'accepted') {
      } else {
      }
      
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  }, [deferredPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);
    safeLocalStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  }, []);

  if (!showPrompt || isInstalled) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden">
        {/* Header avec gradient */}
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Smartphone className="w-5 h-5 text-white" />
            <span className="text-white font-semibold">Installer CareWash GO</span>
          </div>
          <button
            onClick={handleDismiss}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contenu */}
        <div className="p-4">
          <p className="text-gray-600 text-sm mb-4">
            Installez l&apos;application pour un accès rapide et une expérience hors ligne!
          </p>

          {/* Avantages */}
          <div className="flex flex-wrap gap-2 mb-4">
            <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              ✓ Accès hors ligne
            </span>
            <span className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full">
              ✓ Notifications
            </span>
            <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full">
              ✓ Accès rapide
            </span>
          </div>

          {/* Instructions iOS ou bouton d'installation */}
          {isIOS ? (
            <div className="bg-gray-50 rounded-xl p-3">
              <p className="text-sm text-gray-700 mb-2 font-medium">Comment installer:</p>
              <ol className="text-xs text-gray-600 space-y-1">
                <li className="flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Appuyez sur <Share className="w-4 h-4 inline text-blue-500" /> en bas
                </li>
                <li className="flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Sélectionnez &quot;Sur l&apos;écran d&apos;accueil&quot;
                </li>
                <li className="flex items-center gap-2">
                  <span className="bg-orange-100 text-orange-600 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Appuyez sur &quot;Ajouter&quot;
                </li>
              </ol>
            </div>
          ) : (
            <button
              onClick={handleInstall}
              className="w-full bg-gradient-to-r from-orange-500 to-amber-500 text-white font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 hover:shadow-lg transition-shadow"
            >
              <Download className="w-5 h-5" />
              Installer maintenant
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
